import { ImgID } from "../image_loader/img_id.js";
import { LocalizationManager } from "../localization/localization_manager.js";
import type { OfferDto } from "../api/dto/offers.dto.js";

/**
 * Maps the server-declared `visual` of an offer to the client preset that
 * draws it, the way placement_offer_registry does for placement templates.
 * The server validates that a visual exists for the offer's presentation and
 * shop section; this file decides what each one looks like. An offer without
 * a visual keeps the legacy behaviour, where the card style was picked by the
 * offer's position in the list, so the catalog can be migrated one offer at a
 * time without a client release in between.
 */

export interface BundleVisualPreset
{
  coin_image: ImgID;
  title_key: string;
  /** What the "Best" ribbon did before badges were configurable. */
  legacy_best: boolean;
}

const BUNDLE_VISUALS: Record<string, BundleVisualPreset> = {
  bundle_small: { coin_image: ImgID.UI_ShopBundleCoinsSmall, title_key: "BUNDLE.SMALL", legacy_best: true },
  bundle_medium: { coin_image: ImgID.UI_ShopBundleCoinsSmallSet, title_key: "BUNDLE.MEDIUM", legacy_best: false },
  bundle_big: { coin_image: ImgID.UI_ShopBundleCoinsAverage, title_key: "BUNDLE.BIG", legacy_best: false },
  bundle_ultra: { coin_image: ImgID.UI_ShopBundleCoinsLarge, title_key: "BUNDLE.ULTRA", legacy_best: true },
};

/** Legacy styling cycled through this order by list position. */
const LEGACY_BUNDLE_ORDER = [ "bundle_small", "bundle_medium", "bundle_big", "bundle_ultra" ];

export type ResolvedBundleVisual =
  | { kind: "no_ads"; title_key: string; description_key: string }
  | { kind: "bundle"; coin_image: ImgID; title_key: string; best: boolean };

export function resolveBundleVisual( offer: OfferDto, index: number, grants_no_ads: boolean ): ResolvedBundleVisual
{
  const visual = offer.visual;
  if ( visual === "bundle_no_ads" || ( !visual && grants_no_ads ) )
  {
    return {
      kind: "no_ads",
      title_key: offerTitleKey( offer, "REMOVE.ADS" ),
      description_key: offerDescriptionKey( offer, "REMOVE.ADS.TEXT" ),
    };
  }

  const preset = ( visual ? BUNDLE_VISUALS[visual] : undefined ) ?? BUNDLE_VISUALS[LEGACY_BUNDLE_ORDER[index % LEGACY_BUNDLE_ORDER.length]];
  return {
    kind: "bundle",
    coin_image: preset.coin_image,
    title_key: offerTitleKey( offer, preset.title_key ),
    best: resolveBestBadge( offer, preset.legacy_best ),
  };
}

export interface ResolvedDealVisual
{
  title_key: string;
  promotional: boolean;
}

export function resolveDealVisual( offer: OfferDto, column: number ): ResolvedDealVisual
{
  const promotional = offer.visual === "deal_promo_set"
    || ( offer.visual !== "deal_quick_start" && column === 1 );
  return {
    title_key: offerTitleKey( offer, promotional ? "SHOP.OFFER.PROMOTIONAL_SET" : "SHOP.OFFER.QUICK_START" ),
    promotional,
  };
}

/**
 * The badge is configured separately from the visual. An explicit visual with
 * no badge means "no ribbon"; only the legacy auto styling keeps the old
 * hardcoded ribbons so untouched catalogs look exactly as before.
 */
function resolveBestBadge( offer: OfferDto, legacy_best: boolean ): boolean
{
  if ( offer.badge !== undefined ) return offer.badge === "best";
  return offer.visual ? false : legacy_best;
}

export function offerTitleKey( offer: OfferDto, fallback: string ): string
{
  return offer.texts?.title_key ?? fallback;
}

export function offerDescriptionKey( offer: OfferDto, fallback: string ): string
{
  return offer.texts?.description_key ?? fallback;
}

export function offerLocalizedText(
  configured_key: string | undefined,
  fallback_key: string,
  fallback_text: string,
  params: readonly string[] = [],
): string
{
  for ( const key of [ configured_key, fallback_key ] )
  {
    if ( !key ) continue;
    const text = params.length ? LocalizationManager.tWithParams( key, params ) : LocalizationManager.t( key );
    if ( text !== key ) return text;
  }
  return params.reduce( ( acc, value, index ) => acc.split( `{${index}}` ).join( value ), fallback_text );
}

/** Button copy for offers whose button is not the store price (free claims, dialogs). */
export function offerButtonLabel( offer: OfferDto, fallback: string ): string
{
  return offer.texts?.button_key ? LocalizationManager.t( offer.texts.button_key ) : fallback;
}
